import { useCallback, useMemo } from 'react';
import { ITimelineItem, TTimelineRecord } from '../../../../../types/vital-data';
import { TVitalRecordType } from '../../../../../types/life-id';
import { useIntl } from 'react-intl';
import theme from '../../../../../constants/theme';

function extractBasicData(data?: ITimelineItem[]) {
  return {
    history:
      data?.map((item) => ({
        date: item.dateRef,
        value: item.avg,
        description: '--',
      })) || [],
    lastDate: data?.[0]?.dateRef || '--',
    lastValue: data?.[0]?.avg || '--',
  };
}

/**
 * Custom hook to map data for chart
 * @param type type of data
 * @param data data to map
 */
const useChartData = (type: TVitalRecordType, data?: TTimelineRecord) => {
  const intl = useIntl();
  const t = useCallback(
    (id: string) => {
      return intl.formatMessage({ id });
    },
    [intl],
  );

  /**
   * Map data for graph
   */
  return useMemo(() => {
    switch (type) {
      case 'OXYGEN_SATURATION': {
        return {
          units: '%',
          title: 'lifeId:oxygenSaturation',
          typeData: {
            ...extractBasicData(data?.values),
            legends: [
              {
                declension: 'female',
                color: theme.colors.primary,
                text: t('life_id.saturation').toLocaleLowerCase(),
              },
            ],
          },
        };
      }
      case 'BREATHING_RATE': {
        return {
          units: 'brpm',
          title: 'lifeId:breathRate',
          typeData: {
            ...extractBasicData(data?.values),
            legends: [
              {
                declension: 'middle',
                color: theme.colors.primary,
                text: t('life_id.breathRate').toLocaleLowerCase(),
              },
            ],
          },
        };
      }
      case 'GLUCOSE': {
        return {
          units: 'mmol/l',
          title: 'lifeId:glucose',
          typeData: {
            ...extractBasicData(data?.values),
            legends: [
              {
                declension: 'female',
                color: theme.colors.primary,
                text: t('life_id.glucose_legend').toLocaleLowerCase(),
              },
            ],
          },
        };
      }
      case 'BLOOD_PRESSURE': {
        return {
          units: '',
          title: 'lifeId:blood_pressure',
          typeData:
            data && 'systolic' in data
              ? {
                  history:
                    data?.systolic.values.map((item, index) => ({
                      date: item.dateRef,
                      value: `${data?.systolic.values[index]?.avg} / ${data?.diastolic.values[index]?.avg}`,
                      description: '--',
                    })) || [],
                  lastDate: data?.systolic.values[0]?.dateRef || '--',
                  lastValue: data?.systolic.values[0]
                    ? `${data?.systolic.values[0].avg} / ${data?.diastolic.values[0].avg}`
                    : '--',
                  legends: [
                    {
                      declension: 'male',
                      color: theme.colors.success,
                      text: t('life_id.systolic_blood_pressure'),
                    },
                    {
                      declension: 'male',
                      color: 'rgb(106, 213, 229)',
                      text: t('life_id.diastolic_blood_pressure'),
                    },
                  ],
                }
              : {
                  history: [],
                  lastDate: undefined,
                  lastValue: undefined,
                  legends: [
                    {
                      declension: 'male',
                      color: theme.colors.primary,
                      text: t('life_id.systolic_blood_pressure'),
                    },
                    {
                      declension: 'male',
                      color: 'rgb(106, 213, 229)',
                      text: t('life_id.diastolic_blood_pressure'),
                    },
                  ],
                },
        };
      }
      case 'CHOLESTEROL': {
        return {
          units: 'mmol/l',
          title: 'lifeId:cholesterol',
          typeData: {
            ...extractBasicData(data?.values),
            legends: [
              {
                declension: 'male',
                color: theme.colors.primary,
                text: t('life_id.cholesterol').toLocaleLowerCase(),
              },
            ],
          },
        };
      }
      case 'HR': {
        return {
          units: 'bpm',
          title: 'lifeId:heart_rate',
          typeData: {
            ...extractBasicData(data?.values),
            legends: [
              {
                declension: 'male',
                color: theme.colors.primary,
                text: t('life_id.heart_rate').toLocaleLowerCase(),
              },
            ],
          },
        };
      }
      case 'TEMPERATURE':
        return {
          units: '°C',
          title: 'lifeId:body_temperature',
          typeData: {
            ...extractBasicData(data?.values),
            legends: [
              {
                declension: 'female',
                color: theme.colors.primary,
                text: t('life_id.body_temperature_legend').toLocaleLowerCase(),
              },
            ],
          },
        };
      case 'WAIST':
        return {
          units: 'cm',
          title: 'lifeId:waist',
          typeData: {
            ...extractBasicData(data?.values),
            legends: [
              {
                declension: 'male',
                color: theme.colors.primary,
                text: t('life_id.waist_legend').toLocaleLowerCase(),
              },
            ],
          },
        };
      case 'BMI':
        return {
          units: 'bmi',
          title: 'lifeId:bmi',
          typeData: {
            ...extractBasicData(data?.values),
            legends: [
              {
                declension: 'male',
                color: theme.colors.primary,
                text: t('life_id.bmi_legend'),
              },
            ],
          },
        };
      default:
        return {
          units: '',
          title: '',
          typeData: {
            history: [],
            legends: [],
          },
        };
    }
  }, [t, type, data]);
};

export default useChartData;
